import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Sparkles, Maximize2, Minimize2, 
  Trash2, Plus, RotateCcw, Bot, User, Image as ImageIcon,
  Paperclip, ChevronDown, Check, Zap, Brain, Cpu, History, Mic, Square, Loader2,
  ChevronLeft, ChevronRight, Clock, MoreVertical, Pencil,
  Target, Palette, FileText, Share2, TrendingUp, Mail,
  Box, Megaphone, BarChart2, Grid3X3, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  sparkles: Sparkles,
  target: Target,
  palette: Palette,
  'file-text': FileText,
  'share-2': Share2,
  'trending-up': TrendingUp,
  mail: Mail,
  box: Box,
  megaphone: Megaphone,
  'bar-chart-2': BarChart2,
  search: Search,
};

// Color mapping
const COLOR_MAP = {
  teal: 'from-teal-500 to-cyan-500',
  purple: 'from-purple-500 to-violet-500',
  pink: 'from-pink-500 to-rose-500',
  green: 'from-green-500 to-emerald-500',
  blue: 'from-blue-500 to-indigo-500',
  orange: 'from-orange-500 to-amber-500',
  cyan: 'from-cyan-500 to-sky-500',
  indigo: 'from-indigo-500 to-purple-500',
  red: 'from-red-500 to-rose-500',
  slate: 'from-slate-500 to-gray-500',
};

// Available AI Models
const AI_MODELS = [
  { 
    id: 'smart', 
    name: 'Smart Router', 
    description: 'Automatische Modellwahl',
    icon: Zap,
    provider: 'auto',
    model: 'auto',
    color: 'from-teal-500 to-cyan-500'
  },
  { 
    id: 'gpt-5.2', 
    name: 'GPT-5.2', 
    description: 'OpenAI Neueste',
    icon: Sparkles,
    provider: 'openai',
    model: 'gpt-5.2',
    color: 'from-emerald-500 to-teal-500'
  },
  { 
    id: 'claude-sonnet', 
    name: 'Claude Sonnet 4.5', 
    description: 'Anthropic Premium',
    icon: Brain,
    provider: 'anthropic',
    model: 'claude-sonnet-4-5-20250929',
    color: 'from-orange-500 to-amber-500'
  },
  { 
    id: 'gemini-pro', 
    name: 'Gemini 2.5 Pro', 
    description: 'Google Multimodal',
    icon: Cpu,
    provider: 'gemini',
    model: 'gemini-2.5-pro',
    color: 'from-blue-500 to-indigo-500'
  }
];

const QUICK_PROMPTS = {
  general: [
    "Wie optimiere ich meinen CPI auf Meta?",
    "ASO Best Practices für den App Store?",
    "Wie skaliere ich von 5K auf 50K Budget?",
    "UGC Creative Briefing für Fintech App"
  ],
  strategy: [
    "Erstelle mir einen Marketing-Jahresplan",
    "Wie definiere ich Marketing-KPIs?",
    "Budget-Allokation für Multi-Channel Marketing",
    "Wettbewerbsanalyse-Framework"
  ],
  brand: [
    "Wie entwickle ich eine Brand Strategy?",
    "CI/CD Guidelines erstellen",
    "Tone of Voice Definition",
    "Markenpositionierung für Startup"
  ],
  content: [
    "SEO-Content-Strategie entwickeln",
    "Redaktionskalender erstellen",
    "Keyword-Recherche Best Practices",
    "Content-Cluster aufbauen"
  ],
  social: [
    "Instagram vs. TikTok Strategie",
    "Community Management Tipps",
    "Optimale Posting-Zeiten",
    "Hashtag-Strategie entwickeln"
  ],
  performance: [
    "Google Ads Kampagnenstruktur",
    "ROAS-Optimierung Strategien",
    "A/B Testing Framework",
    "Conversion Tracking Setup"
  ],
  crm: [
    "E-Mail-Automation Workflow",
    "Lead-Scoring Modell",
    "Newsletter-Strategie",
    "Segmentierung Best Practices"
  ],
  product: [
    "Go-To-Market Strategie",
    "Launch-Kampagne planen",
    "Competitive Positioning",
    "Sales Enablement Content"
  ],
  pr: [
    "Pressemitteilung schreiben",
    "Media Relations aufbauen",
    "Krisenkommunikation Plan",
    "Event-PR Strategie"
  ],
  analytics: [
    "Marketing-Dashboard erstellen",
    "ROI-Berechnung für Marketing",
    "Attribution Modeling erklärt",
    "KPI-Framework entwickeln"
  ]
};

const MarketingAIChat = ({ embedded = false, onClose }) => {
  const { token } = useAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isExpanded, setIsExpanded] = useState(embedded || !!onClose);
  const [selectedModel, setSelectedModel] = useState(AI_MODELS[0]);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Agent Selection State
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentSelector, setShowAgentSelector] = useState(true); // Default OPEN
  
  // Chat History State
  const [showHistory, setShowHistory] = useState(true); // Default OPEN for embedded
  const [chatSessions, setChatSessions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
    if (embedded) {
      loadChatSessions();
    }
  }, [embedded]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!embedded && isExpanded) {
      textareaRef.current?.focus();
    }
  }, [isExpanded, embedded]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const toggleRecording = async () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const type = recorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type });
        if (blob.size < 1000) return;
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, type.includes('mp4') ? 'aufnahme.mp4' : 'aufnahme.webm');
          const res = await axios.post(`${BACKEND_URL}/api/ai/transcribe`, formData, {
            headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' }
          });
          const text = (res.data?.text || '').trim();
          if (text) setInput(prev => (prev ? prev + ' ' : '') + text);
        } catch (err) {
          console.error('Transcription failed:', err);
        } finally {
          setIsTranscribing(false);
          textareaRef.current?.focus();
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  };

  const loadAgents = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/agents`);
      setAgents(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedAgent(res.data[0]); // Default to first agent (general)
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadChatSessions = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/chat-sessions`, authHeaders);
      setChatSessions(res.data || []);
    } catch (err) {
      console.error('Failed to load chat sessions:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadChatSession = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/chat/${id}`, authHeaders);
      if (res.data && res.data.messages) {
        setSessionId(id);
        setMessages(res.data.messages.map(m => ({
          ...m,
          images: m.has_images ? [] : null
        })));
        // Set the agent from the session if available
        if (res.data.agent_id && agents.length > 0) {
          const sessionAgent = agents.find(a => a.id === res.data.agent_id);
          if (sessionAgent) {
            setSelectedAgent(sessionAgent);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load chat session:', err);
    }
  };

  const deleteChatSession = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/ai/chat/${id}`, authHeaders);
      setChatSessions(prev => prev.filter(s => s.session_id !== id));
      if (sessionId === id) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Failed to delete chat session:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 1568;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      resolve({ dataUrl, base64: dataUrl.split(',')[1], type: 'image/jpeg' });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidFormat = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
      const isValidSize = file.size < 10 * 1024 * 1024;
      return isImage && isValidFormat && isValidSize;
    });

    if (validFiles.length !== files.length) {
      alert('Nur JPEG, PNG und WEBP Bilder unter 10MB sind erlaubt.');
    }

    for (const file of validFiles) {
      const compressed = await compressImage(file);
      if (!compressed) continue;
      setAttachedFiles(prev => [...prev, {
        name: file.name,
        type: compressed.type,
        base64: compressed.base64
      }]);
      setPreviewImages(prev => [...prev, compressed.dataUrl]);
    }

    e.target.value = '';
  }, []);

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (text) => {
    const messageText = text || input.trim();
    if ((!messageText && attachedFiles.length === 0) || isLoading) return;

    const userMsg = { 
      role: 'user', 
      content: messageText, 
      images: previewImages.length > 0 ? [...previewImages] : null,
      timestamp: new Date().toISOString() 
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    
    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    setPreviewImages([]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        message: messageText,
        session_id: sessionId,
        model_id: selectedModel.id,
        provider: selectedModel.provider,
        model: selectedModel.model,
        agent_id: selectedAgent?.id || 'general',
        images: filesToSend.map(f => ({
          base64: f.base64,
          mime_type: f.type
        }))
      }, authHeaders);
      
      const aiMsg = { 
        role: 'assistant', 
        content: res.data.response, 
        model_used: res.data.model_used,
        agent_name: res.data.agent_name,
        timestamp: new Date().toISOString() 
      };
      setMessages(prev => [...prev, aiMsg]);
      
      if (!sessionId) {
        setSessionId(res.data.session_id);
        loadChatSessions();
      }
    } catch (err) {
      const errorMsg = { 
        role: 'assistant', 
        content: err.response?.data?.detail || 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut.', 
        timestamp: new Date().toISOString() 
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId('');
    setInput('');
    setAttachedFiles([]);
    setPreviewImages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessage = (text) => {
    if (!text) return '';
    let formatted = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n- /g, '</p><li class="ml-4 list-disc">')
      .replace(/\n(\d+)\. /g, '</p><li class="ml-4 list-decimal">')
      .replace(/\n/g, '<br/>');
    return `<p class="mb-2">${formatted}</p>`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Gerade eben';
    if (diff < 3600000) return `vor ${Math.floor(diff / 60000)} Min.`;
    if (diff < 86400000) return `vor ${Math.floor(diff / 3600000)} Std.`;
    if (diff < 604800000) return `vor ${Math.floor(diff / 86400000)} Tagen`;
    return date.toLocaleDateString('de-DE');
  };

  const getAgentIcon = (iconName) => {
    return ICON_MAP[iconName] || Sparkles;
  };

  const getAgentColor = (colorName) => {
    return COLOR_MAP[colorName] || COLOR_MAP.teal;
  };

  if (!embedded && !isExpanded) {
    return null;
  }

  const chatHeight = embedded ? 'h-full' : 'h-[600px] max-h-[70vh]';
  const AgentIcon = selectedAgent ? getAgentIcon(selectedAgent.icon) : Sparkles;
  const agentColor = selectedAgent ? getAgentColor(selectedAgent.color) : COLOR_MAP.teal;

  return (
    <div className={cn(
      'flex bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden',
      embedded ? 'w-full h-full' : 'w-[800px] max-w-[calc(100vw-1rem)]',
      chatHeight
    )} data-testid="marketing-ai-chat-container">
      
      {/* Left Sidebar: Agent Selection & History */}
      {embedded && (
        <div className={cn(
          "border-r border-slate-200 bg-slate-50 flex flex-col transition-all duration-300",
          showHistory ? "w-80" : "w-0 overflow-hidden"
        )}>
          {/* Agent Selector */}
          <div className="p-3 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500">Spezialist wählen</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAgentSelector(!showAgentSelector)}
                className="h-6 px-2 text-xs"
              >
                <Grid3X3 className="w-3 h-3 mr-1" />
                Alle
              </Button>
            </div>
            
            {/* Current Agent */}
            {selectedAgent && (
              <div className={cn(
                "p-2 rounded-lg border-2 border-transparent bg-gradient-to-r",
                agentColor,
                "text-white"
              )}>
                <div className="flex items-center gap-2">
                  <AgentIcon className="w-5 h-5" />
                  <div>
                    <p className="text-sm font-semibold">{selectedAgent.name}</p>
                    <p className="text-[10px] opacity-80">{selectedAgent.description}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Agent Grid (collapsible) */}
          <AnimatePresence>
            {showAgentSelector && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden border-b border-slate-200"
              >
                <div className="p-2 grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
                  {agents.map((agent) => {
                    const Icon = getAgentIcon(agent.icon);
                    const color = getAgentColor(agent.color);
                    const isSelected = selectedAgent?.id === agent.id;
                    
                    return (
                      <button
                        key={agent.id}
                        onClick={() => {
                          setSelectedAgent(agent);
                          setShowAgentSelector(false);
                          handleNewChat();
                        }}
                        className={cn(
                          "p-2 rounded-lg text-left transition-all",
                          isSelected 
                            ? "bg-slate-900 text-white" 
                            : "bg-white hover:bg-slate-100 border border-slate-200"
                        )}
                      >
                        <div className={cn(
                          "w-6 h-6 rounded-md bg-gradient-to-br flex items-center justify-center mb-1",
                          color
                        )}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-[10px] font-medium truncate">{agent.name}</p>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat History Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-xs text-slate-700">Chat-Verlauf</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNewChat}
              className="h-7 px-2 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Neu
            </Button>
          </div>
          
          {/* Chat List */}
          <div className="flex-1 overflow-y-auto">
            {loadingHistory ? (
              <div className="p-4 text-center text-sm text-slate-400">Laden...</div>
            ) : chatSessions.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-400">Keine gespeicherten Chats</div>
            ) : (
              <div className="p-2 space-y-1">
                {chatSessions.map((session) => {
                  const SessionAgentIcon = session.agent_id ? getAgentIcon(
                    agents.find(a => a.id === session.agent_id)?.icon || 'sparkles'
                  ) : Sparkles;
                  
                  return (
                    <div
                      key={session.session_id}
                      className={cn(
                        "group relative p-2 rounded-lg cursor-pointer transition-colors",
                        sessionId === session.session_id 
                          ? "bg-teal-50 border border-teal-200" 
                          : "hover:bg-slate-100"
                      )}
                      onClick={() => loadChatSession(session.session_id)}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-md bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <SessionAgentIcon className="w-3 h-3 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">
                            {session.title || 'Unbenannter Chat'}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-[10px] text-slate-400">
                              {session.agent_name || 'marKI Guru'}
                            </span>
                            <span className="text-[10px] text-slate-300">•</span>
                            <span className="text-[10px] text-slate-400">
                              {formatDate(session.updated_at)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteChatSession(session.session_id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-500 transition-all"
                          title="Chat löschen"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            {embedded && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white mr-1"
                title={showHistory ? "Sidebar ausblenden" : "Sidebar anzeigen"}
              >
                {showHistory ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
            
            <div className={cn("w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center", agentColor)}>
              <AgentIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-display text-sm font-semibold">
                {selectedAgent?.name || 'marKI Guru'}
              </h3>
              <p className="text-[10px] text-slate-400">
                {selectedAgent?.description || 'Ihr Marketing-Berater'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Model Selector */}
            <div className="relative">
              <button 
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-300"
              >
                <selectedModel.icon className="w-3 h-3" />
                <span className="hidden sm:inline">{selectedModel.name}</span>
                <ChevronDown className={cn("w-3 h-3 transition-transform", showModelSelector && "rotate-180")} />
              </button>
              
              <AnimatePresence>
                {showModelSelector && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute right-0 top-full mt-1 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-hidden z-50"
                  >
                    {AI_MODELS.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => {
                          setSelectedModel(model);
                          setShowModelSelector(false);
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-700 transition-colors",
                          selectedModel.id === model.id && "bg-slate-700"
                        )}
                      >
                        <div className={cn("w-6 h-6 rounded-md bg-gradient-to-br flex items-center justify-center", model.color)}>
                          <model.icon className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{model.name}</p>
                          <p className="text-[10px] text-slate-400">{model.description}</p>
                        </div>
                        {selectedModel.id === model.id && (
                          <Check className="w-3 h-3 text-teal-400 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <button onClick={handleNewChat} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" title="Neuer Chat">
              <Plus className="w-4 h-4" />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="marketing-ai-chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-6">
              <div className={cn("w-16 h-16 rounded-2xl bg-gradient-to-br flex items-center justify-center mx-auto mb-4 shadow-lg", agentColor)}>
                <AgentIcon className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-display text-lg font-semibold text-slate-900 mb-1">
                {selectedAgent?.name || 'marKI Guru'}
              </h4>
              <p className="text-xs text-slate-500 mb-2 max-w-sm mx-auto">
                {selectedAgent?.description || 'Ihr persönlicher Marketing-Berater'}
              </p>
              {selectedAgent?.tools && (
                <p className="text-[10px] text-slate-400 mb-4">
                  Tools: {selectedAgent.tools}
                </p>
              )}
              <div className="space-y-2 max-w-sm mx-auto">
                {(QUICK_PROMPTS[selectedAgent?.id] || QUICK_PROMPTS.general).map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(prompt)}
                    className="w-full text-left px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 hover:bg-slate-100 hover:border-slate-300 transition-colors"
                    data-testid={`quick-prompt-${i}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              {msg.role === 'assistant' && (
                <div className={cn("w-7 h-7 rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0 mt-0.5", agentColor)}>
                  <AgentIcon className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={cn(
                'max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed',
                msg.role === 'user' 
                  ? 'bg-slate-900 text-white rounded-br-md' 
                  : 'bg-slate-50 border border-slate-200 text-slate-800 rounded-bl-md'
              )}>
                {msg.images && msg.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {msg.images.map((img, idx) => (
                      <img 
                        key={idx} 
                        src={img} 
                        alt={`Attached ${idx + 1}`} 
                        className="w-20 h-20 object-cover rounded-lg border border-white/20"
                      />
                    ))}
                  </div>
                )}
                {msg.role === 'assistant' ? (
                  <>
                    <div 
                      className="prose prose-sm prose-slate max-w-none [&_strong]:font-semibold [&_li]:text-[13px] [&_p]:mb-1.5 [&_p:last-child]:mb-0" 
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} 
                    />
                    {(msg.model_used || msg.agent_name) && (
                      <p className="text-[9px] text-slate-400 mt-2 pt-2 border-t border-slate-200">
                        {msg.agent_name && <span>{msg.agent_name}</span>}
                        {msg.agent_name && msg.model_used && <span> • </span>}
                        {msg.model_used && <span>via {msg.model_used}</span>}
                      </p>
                    )}
                  </>
                ) : (
                  <p>{msg.content}</p>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-3.5 h-3.5 text-slate-600" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-2 justify-start">
              <div className={cn("w-7 h-7 rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0", agentColor)}>
                <AgentIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Attachment Preview */}
        {previewImages.length > 0 && (
          <div className="px-3 py-2 border-t border-slate-200 bg-slate-50">
            <div className="flex gap-2 flex-wrap">
              {previewImages.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img} alt={`Preview ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                  <button
                    onClick={() => removeAttachment(idx)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-slate-200 p-3 flex-shrink-0">
          <div className="flex gap-2 items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="h-10 w-10 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
              title="Bild hochladen"
            >
              <ImageIcon className="w-5 h-5" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleRecording}
              disabled={isTranscribing}
              className={cn(
                "h-10 w-10 p-0 flex-shrink-0",
                isRecording
                  ? "text-red-500 bg-red-50 hover:bg-red-100 animate-pulse"
                  : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              )}
              title={isRecording ? 'Aufnahme stoppen' : 'Spracheingabe starten'}
              data-testid="voice-input-button"
            >
              {isTranscribing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isRecording ? (
                <Square className="w-4 h-4 fill-current" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </Button>
            
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isRecording ? 'Ich höre zu… (Stopp drücken zum Übernehmen)' : `Frage an ${selectedAgent?.name || 'marKI Guru'}...`}
              className="flex-1 min-h-[40px] max-h-[150px] resize-none text-sm py-2.5 overflow-y-auto"
              disabled={isLoading}
              rows={1}
              data-testid="marketing-ai-chat-input"
            />
            
            <Button 
              onClick={() => handleSend()} 
              disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
              className="h-10 w-10 p-0 bg-slate-900 hover:bg-slate-800 flex-shrink-0"
              data-testid="marketing-ai-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center">
            {selectedAgent?.name || 'marKI Guru'} • {selectedModel.name}
          </p>
        </div>
      </div>
    </div>
  );
};

// Floating Chat Widget
export const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  if (!user) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 left-2 right-2 sm:left-auto sm:right-20 z-[9998] flex justify-center sm:justify-end"
          >
            <MarketingAIChat onClose={() => setIsOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-4 right-4 sm:right-20 z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors",
          isOpen ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-900 hover:bg-slate-800"
        )}
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="floating-chat-widget-button"
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <div className="relative">
            <Sparkles className="w-5 h-5 text-white" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-400 rounded-full animate-pulse" />
          </div>
        )}
      </motion.button>
    </>
  );
};

export default MarketingAIChat;
